import { useCallback, useRef, useState } from 'react';
import type { ReplayState } from './ReplayEngine';

interface ReplayControlsProps {
  state: ReplayState | null;
  onPlayPause: () => void;
  onSeek: (percent: number) => void;
  onSpeedChange: (speed: number) => void;
  onRestart: () => void;
  disabled?: boolean;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = Math.floor((ms % 1000) / 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
}

const SPEEDS = [0.5, 1, 2];

export default function ReplayControls({
  state,
  onPlayPause,
  onSeek,
  onSpeedChange,
  onRestart,
  disabled = false,
  isMuted = true,
  onToggleMute,
}: ReplayControlsProps) {
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPercent, setHoverPercent] = useState<number | null>(null);

  const progress = state ? (state.elapsed / state.duration) * 100 : 0;
  const elapsed = state?.elapsed ?? 0;
  const duration = state?.duration ?? 45000;
  const isPlaying = state?.isPlaying ?? false;
  const currentSpeed = state?.playbackSpeed ?? 1;

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    onSeek(percent);
  }, [disabled, onSeek]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    setHoverPercent(percent);

    if (isDragging && !disabled) {
      onSeek(percent);
    }
  }, [isDragging, disabled, onSeek]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    setIsDragging(true);
    handleProgressClick(e);
  }, [disabled, handleProgressClick]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoverPercent(null);
    setIsDragging(false);
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {/* Progress bar */}
      <div className="relative group">
        {/* Time preview on hover */}
        {hoverPercent !== null && (
          <div
            className="absolute -top-8 transform -translate-x-1/2 pointer-events-none z-10"
            style={{ left: `${hoverPercent}%` }}
          >
            <div className="bg-black/90 border border-white/20 rounded px-2 py-1 text-xs font-mono text-white whitespace-nowrap">
              {formatTime((hoverPercent / 100) * duration)}
            </div>
          </div>
        )}

        <div
          ref={progressRef}
          className={`relative h-2 rounded-full overflow-hidden cursor-pointer transition-all ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:h-3'
          }`}
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          onClick={handleProgressClick}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Progress fill */}
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${Math.max(progress, 0.5)}%`,
              background: 'linear-gradient(90deg, #22c55e 0%, #86efac 100%)',
              minWidth: '4px',
            }}
          />

          {/* Hover indicator */}
          {hoverPercent !== null && !disabled && (
            <div
              className="absolute inset-y-0 bg-white/20 pointer-events-none"
              style={{
                left: `${Math.min(progress, hoverPercent)}%`,
                width: `${Math.abs(hoverPercent - progress)}%`,
              }}
            />
          )}

          {/* Scrubber handle */}
          <div
            className={`absolute w-3 h-3 bg-white rounded-full shadow-lg ${
              disabled ? 'opacity-0' : 'group-hover:scale-125'
            }`}
            style={{
              left: `${progress}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              boxShadow: '0 0 8px rgba(34, 197, 94, 0.5)',
              transition: 'transform 0.15s, opacity 0.15s',
            }}
          />
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between">
        {/* Left: Time display */}
        <div className="flex items-center gap-2 min-w-[140px]">
          <span className="font-mono text-sm text-white tabular-nums">
            {formatTime(elapsed)}
          </span>
          <span className="text-white/30">/</span>
          <span className="font-mono text-sm text-white/50 tabular-nums">
            {formatTime(duration)}
          </span>
        </div>

        {/* Center: Playback controls */}
        <div className="flex items-center gap-2">
          {/* Restart button */}
          <button
            onClick={onRestart}
            disabled={disabled}
            className={`p-2 rounded-lg transition-all ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-white/10 active:scale-95'
            }`}
            title="Restart"
          >
            <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>

          {/* Play/Pause button */}
          <button
            onClick={onPlayPause}
            disabled={disabled}
            className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              disabled
                ? 'bg-white/5 opacity-50 cursor-not-allowed'
                : 'bg-white/10 hover:bg-white/20 active:scale-95'
            }`}
            style={{
              boxShadow: disabled ? 'none' : '0 0 20px rgba(34, 197, 94, 0.2)',
            }}
          >
            {/* Glow ring */}
            <div
              className={`absolute inset-0 rounded-full transition-opacity ${
                isPlaying && !disabled ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
              }}
            />

            {isPlaying ? (
              // Pause icon
              <svg className="w-5 h-5 text-white relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              // Play icon (slightly offset for visual balance)
              <svg className="w-5 h-5 text-white relative z-10 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
              </svg>
            )}
          </button>
        </div>

        {/* Right: Speed selector and Mute */}
        <div className="flex items-center gap-3 min-w-[180px] justify-end">
          {/* Mute button */}
          {onToggleMute && (
            <button
              onClick={onToggleMute}
              disabled={disabled}
              className={`p-2 rounded-lg transition-all ${
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/10 active:scale-95'
              }`}
              title={isMuted ? 'Unmute audio' : 'Mute audio'}
              aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            >
              {isMuted ? (
                <svg
                  className="w-4 h-4 text-white/50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-[#22c55e]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
          )}

          {/* Speed selector */}
          <div className="flex items-center gap-1">
            <span className="text-white/40 text-xs mr-2 uppercase tracking-wider">Speed</span>
            {SPEEDS.map((speed) => (
              <button
                key={speed}
                onClick={() => onSpeedChange(speed)}
                disabled={disabled}
                className={`px-2.5 py-1 rounded text-xs font-mono transition-all ${
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : currentSpeed === speed
                      ? 'bg-[#22c55e] text-black font-semibold'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
